import React, { useState, useEffect } from "react";
import { href, Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { motion, AnimatePresence } from "framer-motion";
import {
    Menu,
    Search,
    MessageCircle,
    Bookmark,
    Bell,
    User,
    Settings,
    LogOut,
    ChevronRight,
    Home,
    Users,
    Globe,
    Phone,
    FileText,
    Shield,
    Wallet,
    History,
    Star,
    Lock,
    X,
    UserCog,
    Clock,
    ChevronDown,
    User2,
    Settings2,
    AlertCircle,
    Briefcase,
    FileCheck,
    BarChart as ChartAreaIcon,
    AlignCenter,
    StarIcon,
    Verified,
    DollarSign,
    Contact,
    UserSearchIcon,
} from "lucide-react";
import { MdReviews } from "react-icons/md";
import { LogoutManually, LogoutUser, useAppDispatch, useAppSelector } from "../redux";
import SocialMediaLinks from "../components/ui/SocialMediaLinks";
import { RoleEnum } from "../modules";
import LanguageSelector from "../components/LanguageSelector";

const SharedHeader4 = () => {
    const { t } = useTranslation();
    const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
    const { unreadCount } = useAppSelector((state) => state.userNotifications);
    const { user } = useAppSelector((state) => state.auth);

    const dispatch = useAppDispatch();
    const navigate = useNavigate();

    const menuItems = [
        { 
            icon: <Home className="h-5 w-5" />, 
            label: t("sidebar.dashboard"), 
            href: "/professionals", 
            active: true,
            rightIcon: <Home className="h-4 w-4 text-gray-400" />,
            rightAction: () => navigate("/"),
        },
        { icon: <UserSearchIcon className="h-5 w-5" />, label: t("sidebar.localCustomers"), href: "/professional" },
        {
            icon: <Bookmark className="h-5 w-5" />,
            label: t("sidebar.internationalClients"),
            href: "/international-professionals",
        },
        { icon: <User2 className="h-5 w-5" />, label: t("sidebar.myProfile"), href: "/myprofile" },
        { icon: <Settings2 className="h-5 w-5" />, label: t("sidebar.mySettings"), href: "/Settings" },
        { icon: <FileCheck className="h-5 w-5" />, label: t("sidebar.myOrders"), href: "/myorders" },
        { icon: <Bell className="h-5 w-5" />, label: t("sidebar.myOffers"), href: "/myoffers" },
        { icon: <FileText className="h-5 w-5" />, label: t("sidebar.myProposalsWhatsapp"), href: "/myproposals" },
        { icon: <StarIcon className="h-5 w-5" />, label: t("sidebar.favorites"), href: "/professional/favorites" },
        { icon: <ChartAreaIcon className="h-5 w-5" />, label: t("sidebar.progress"), href: "/Progress" },
        { icon: <FileCheck className="h-5 w-5" />, label: t("sidebar.subscriptions"), href: "/credits" },
        { icon: <MessageCircle className="h-5 w-5" />, label: t("sidebar.messageOrVideoCall"), href: "/MessageAndVideoCall" },
        { icon: <MdReviews className="h-5 w-5" />, label: t("sidebar.reviews"), href: "/professional/reviews" },
        { icon: <Wallet className="h-5 w-5" />, label: t("sidebar.wallet"), href: "/wallet" },
        { icon: <FileText className="h-5 w-5" />, label: t("sidebar.myInvoices"), href: "/invoices" },
        { icon: <FileText className="h-5 w-5" />, label: t("sidebar.withdrawHistory"), href: "/withdrawalhistroy" },
        { icon: <Wallet className="h-5 w-5" />, label: t("sidebar.refundRequest"), href: "/refund-requests" },
        {
            icon: <Verified className="h-5 w-5" />,
            label: t("sidebar.verifyAccount"),
            href: "/verify",
        },
        { icon: <Shield className="h-5 w-5" />, label: t("sidebar.supportTickets"), href: "/support" },
        { icon: <Contact className="h-5 w-5" />, label: t("sidebar.contactUs"), href: "/menu/contact" },
        // { icon: <Wallet className="h-5 w-5" />, label: t("sidebar.report"), href: "/client/reports" },
        { icon: <Lock className="h-5 w-5" />, label: t("sidebar.changePassword"), href: "/changepassword" },
        { icon: <AlertCircle className="h-5 w-5" />, label: t("sidebar.accountDelete"), href: "/DeleteAccount" },
        { icon: <LogOut className="h-5 w-5" />, label: t("sidebar.logOut"), href: "/login" },
    ];

    const handleLogout = async () => {
        try {
            const response = await dispatch(LogoutUser({})).unwrap();
        } catch (error: any) {
            if (error === 401) {
                dispatch(LogoutManually());
                console.log("Token expired");
            } else {
                console.log("Logout failed:", error);
            }
        } finally {
            navigate("/");
        }
    };

    const StoreLinks = () => (
        <div className="grid grid-cols-2 gap-2 sm:gap-3 pt-4 px-2 sm:px-4 w-full">
            <a href="https://play.google.com/store/apps/details?id=com.kokobeo" className="flex justify-center">
                <img
                    src="https://texttofloss.com/wp-content/uploads/2021/01/Google-Play-Store-Button.png"
                    alt="Get it on Google Play"
                    className="w-full max-w-[140px] h-auto rounded-lg hover:opacity-90 transition-opacity"
                />
            </a>
            <a href="https://apps.apple.com/app/kokobeo" className="flex justify-center">
                <img
                    src="https://e7.pngegg.com/pngimages/422/842/png-clipart-apple-store-logo-app-store-android-google-play-get-started-now-button-text-logo.png"
                    alt="Download on App Store"
                    className="w-full max-w-[140px] h-auto rounded-lg hover:opacity-90 transition-opacity"
                />
            </a>
        </div>
    );

    const SocialIcons = () => (
        <SocialMediaLinks containerSize="w-7 h-7"  className="flex justify-center space-x-3 sm:space-x-4 pt-4 border-t border-gray-100 px-2 sm:px-4 pb-6 w-full" iconSize="h-4 w-4 sm:h-5 sm:w-5" />
    );

    return (
        <header className="bg-white border-b sticky top-0 z-50 shadow-sm w-full">
            <div className="max-w-7xl mx-auto px-2 sm:px-4 w-full">
                <div className="flex justify-between h-14 sm:h-16 w-full">
                    {/* Left section */}
                    <div className="flex items-center gap-2 sm:gap-4 w-auto">
                        <button
                            onClick={() => setIsMobileMenuOpen(true)}
                            className="lg:hidden p-1.5 sm:p-2 text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
                            aria-label="Open menu">
                            <Menu className="h-5 w-5 sm:h-6 sm:w-6" />
                        </button>

                        <Link to={Number(user?.role?.id) === RoleEnum.client ? "/clients" : "/professionals"} className="flex items-center w-auto">
                            <img
                                src="https://assests.netlify.app/assets/images/logo.png"
                                alt="Kokobeo Logo"
                                className="h-6 w-6 sm:h-8 sm:w-8"
                            />
                            <span className="ml-1 sm:ml-2 text-blue-600 text-lg sm:text-xl font-bold">Kokobeo</span>
                        </Link>
                    </div>

                    {/* Right section */}
                    <div className="flex items-center gap-2 sm:gap-4 w-auto">
                        {/* Language Selector */}
                        <LanguageSelector variant="modal" />

                        {/* Notification Icon */}
                        <button
                            className="p-1.5 sm:p-2 text-gray-600 hover:bg-gray-100 rounded-lg relative transition-colors"
                            aria-label="Notifications"
                            onClick={() => navigate("/notifications")}>
                            <Bell className="h-5 w-5" />
                            {unreadCount > 0 && (
                                <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs font-bold rounded-full h-5 w-5 flex items-center justify-center">
                                    {unreadCount > 99 ? "99+" : unreadCount}
                                </span>
                            )}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile Menu Modal */}
            <AnimatePresence>
                {isMobileMenuOpen && (
                    <>
                        <motion.div
                            initial={{ opacity: 0 }}
                            animate={{ opacity: 1 }}
                            exit={{ opacity: 0 }}
                            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-40 lg:hidden"
                            onClick={() => setIsMobileMenuOpen(false)}
                        />
                        <motion.div
                            initial={{ x: "-100%" }}
                            animate={{ x: 0 }}
                            exit={{ x: "-100%" }}
                            transition={{ type: "spring", bounce: 0, duration: 0.3 }}
                            className="fixed inset-0 left-0 w-[270px] sm:w-[320px] max-w-xs bg-white z-50 shadow-xl flex flex-col">
                            <div className="flex-none p-4 border-b border-gray-100">
                                <div className="flex items-center justify-between w-full">
                                    <div className="flex items-center gap-2">
                                        <img
                                            src="https://assests.netlify.app/assets/images/logo.png"
                                            alt="Logo"
                                            className="h-6 w-6 sm:h-8 sm:w-8"
                                        />
                                        <span className="text-blue-600 text-lg sm:text-xl font-bold">Kokobeo</span>
                                    </div>
                                    <button
                                        onClick={() => setIsMobileMenuOpen(false)}
                                        className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
                                        aria-label="Close menu">
                                        <X className="h-5 w-5 sm:h-6 sm:w-6 text-gray-600" />
                                    </button>
                                </div>
                            </div>

                            {/* Menu Items - Scrollable Area */}
                            <div className="flex-1 overflow-y-auto">
                                <nav className="p-2 sm:p-3">
                                    {menuItems.map((item, index) =>
                                        item.href == "/login" ? (
                                            <button
                                                key={index}
                                                onClick={handleLogout}
                                                className="flex w-full items-center gap-2 px-3 py-2 rounded-lg text-sm transition-colors text-gray-600 hover:bg-gray-50">
                                                {item.icon}
                                                <span>{item.label}</span>
                                            </button>
                                        ) : (
                                            <div key={index} className="flex items-center justify-between px-3 py-2.5 text-gray-700 hover:bg-blue-50 hover:text-blue-600 rounded-lg transition-colors w-full text-sm mb-1.5">
                                                <Link
                                                    to={item.href}
                                                    className="flex items-center gap-3 flex-1"
                                                    onClick={() => setIsMobileMenuOpen(false)}>
                                                    {item.icon}
                                                    <span className="font-medium whitespace-normal break-words">
                                                        {item.label}
                                                    </span>
                                                </Link>
                                                {item.rightIcon && (
                                                    <button
                                                        onClick={(e) => {
                                                            e.preventDefault();
                                                            e.stopPropagation();
                                                            if (item.rightAction) {
                                                                item.rightAction();
                                                            }
                                                            setIsMobileMenuOpen(false);
                                                        }}
                                                        className="p-1 hover:bg-blue-100 rounded transition-colors ml-2">
                                                        {item.rightIcon}
                                                    </button>
                                                )}
                                            </div>
                                        )
                                    )}
                                </nav>
                            </div>

                            {/* Footer with store links and social icons */}
                            <div className="flex-none border-t border-gray-100 pt-2 bg-white">
                                <StoreLinks />
                                <SocialIcons />
                            </div>
                        </motion.div>
                    </>
                )}
            </AnimatePresence>
        </header>
    );
};

export default SharedHeader4;
